import { Pressable, ScrollView, Text, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useKeepAwake } from 'expo-keep-awake'
import Head from 'expo-router/head'
import { StatusBar } from 'expo-status-bar'
import { useTranslation } from 'react-i18next'

import type {
  PostPaymentConfirmedRecordingChange,
  PostPaymentRestoredRecording,
} from '../post-payment-recording'
import { BetaBadge } from '../../../components/ui/BetaBadge'
import { Button } from '../../../components/ui/Button'
import { Icon, symbolName } from '../../../components/ui/Icon'
import { colors } from '../../../theme'
import { body3, label2 } from '../../../theme/typography'
import { usePostPaymentRecordController } from '../hooks/usePostPaymentRecordController'
import {
  RecordReadyState,
  RecordRecordingState,
  RecordSucceededState,
} from './PostPaymentRecordStep.ui'

const NON_SCALING_TEXT_PROPS = {
  allowFontScaling: false,
  maxFontSizeMultiplier: 1,
} as const

const CONTROL_BORDER_RADIUS = 8
const TITLE_SUBTITLE_GAP = 25
const CONTENT_BOX_PADDING = 16
const CONTENT_BOX_GAP = 18
const SECTION_GAP = 26

const TITLE_STYLE = {
  fontFamily: 'Cairo_700Bold',
  fontSize: 24,
  lineHeight: 32,
  color: colors.textHeader,
} as const

const SUBTITLE_STYLE = {
  fontFamily: 'OpenSans_400Regular',
  fontSize: 16,
  lineHeight: 18,
  color: colors.textBody,
} as const

export interface PostPaymentRecordStepProps {
  audioTrack?: string | null
  onNext: () => void
  onSkip: () => void
  isSubmitting: boolean
  showProgress?: boolean
  progressCurrentStep?: number
  progressTotalSteps?: number
  /** Left-aligned step title beside Skip (e.g. voice assessment flow). */
  stepHeaderLabel?: string
  /** Override center title; pass null to hide it (defaults to record.title). */
  recordingCenterTitle?: string | null
  recordingCenterSubtitle?: string
  /** Voice-assessment beta marker; generic onboarding record steps opt out. */
  showBetaBadge?: boolean
  /** Resume: open with a confirmed clip already in place. */
  restoredRecording?: PostPaymentRestoredRecording | null
  /**
   * Notified when the user confirms a recording (with the captured Blob)
   * or clears it (redo / new take).
   */
  onConfirmedRecordingChange?: PostPaymentConfirmedRecordingChange
  /** Measurement-specific vowel guidance; the card is hidden when empty. */
  recordingInstruction?: string | null
}

export function PostPaymentRecordStep({
  audioTrack,
  onNext,
  onSkip,
  isSubmitting,
  showProgress = true,
  progressCurrentStep,
  progressTotalSteps,
  stepHeaderLabel,
  recordingCenterTitle,
  recordingCenterSubtitle,
  showBetaBadge = false,
  restoredRecording,
  onConfirmedRecordingChange,
  recordingInstruction,
}: PostPaymentRecordStepProps) {
  const { t } = useTranslation(['onboarding', 'common'])
  const insets = useSafeAreaInsets()
  const footerPaddingBottom = Math.max(insets.bottom, 8)
  const assessmentProgressSteps =
    progressTotalSteps != null
      ? Array.from({ length: progressTotalSteps }, (_, index) => index + 1)
      : []

  const {
    uiState,
    isPianoPlaying,
    isExamplePlaying,
    isRecordedAudioPlaying,
    elapsedRecordingSeconds,
    handlePlayPiano,
    handlePlayExample,
    handleStartRecording,
    handleStopRecording,
    handleListenToRecording,
    handleRedoRecording,
    handleRetryHelperAction,
  } = usePostPaymentRecordController({
    audioTrack,
    restoredRecording,
    onConfirmedRecordingChange,
  })

  const helperActionErrorMessage =
    uiState.failedHelperAction === 'play_piano'
      ? t('onboarding:record.piano_helper_action_failed')
      : uiState.failedHelperAction === 'play_example'
        ? t('onboarding:record.example_helper_action_failed')
        : t('onboarding:record.helper_action_failed')

  const recordFlowErrorMessage =
    uiState.recordFlowErrorKey === 'permission'
      ? t('onboarding:record.permission_required')
      : uiState.recordFlowErrorKey === 'start'
        ? t('onboarding:record.start_recording_failed')
        : uiState.recordFlowErrorKey === 'stop'
          ? t('onboarding:record.stop_recording_failed')
          : uiState.recordFlowErrorKey === 'playback'
            ? t('onboarding:record.playback_failed')
            : null

  const elapsedRecordingTimeLabel = t('onboarding:record.elapsed_recording', {
    seconds: elapsedRecordingSeconds,
  })
  const isNextDisabled = isSubmitting || uiState.recordedAudioUri == null

  return (
    <View
      className="bg-background-primary flex-1"
      style={{
        flex: 1,
        backgroundColor: colors.backgroundPrimary,
        paddingTop: insets.top,
      }}
    >
      {uiState.step === 'recording' && <KeepAwakeController />}
      <Head>
        <title>{t('common:page_title_onboarding')}</title>
      </Head>
      <StatusBar style="dark" />
      <View className="px-6 pt-6">
        <View className="flex-row items-center justify-between mb-3 gap-2">
          {stepHeaderLabel != null ? (
            <Text
              {...NON_SCALING_TEXT_PROPS}
              numberOfLines={2}
              style={[label2, { color: colors.textMuted, flex: 1 }]}
            >
              {stepHeaderLabel}
            </Text>
          ) : (
            <View className="flex-1" />
          )}
          <Pressable onPress={onSkip} className="active:opacity-60">
            <Text
              {...NON_SCALING_TEXT_PROPS}
              style={[label2, { color: colors.textBody }]}
            >
              {t('onboarding:voice_assessment.skip_header')}
            </Text>
          </Pressable>
        </View>
        {showProgress &&
          (progressCurrentStep != null && progressTotalSteps != null ? (
            <View className="flex-row items-center gap-2">
              {assessmentProgressSteps.map((stepNumber) => (
                <View
                  key={`assessment-progress-${stepNumber}`}
                  className={`flex-1 rounded-full ${stepNumber <= progressCurrentStep ? 'bg-primary' : 'bg-border'}`}
                  style={{ height: 9 }}
                />
              ))}
            </View>
          ) : null)}
      </View>

      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        style={{ flex: 1, backgroundColor: colors.backgroundPrimary }}
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: 24,
          paddingTop: 26,
          paddingBottom: 24,
        }}
        showsVerticalScrollIndicator={false}
      >
        <View
          className="items-center"
          style={{ gap: SECTION_GAP, width: '100%' }}
        >
          <View
            style={{
              gap: TITLE_SUBTITLE_GAP,
              alignItems: 'center',
              paddingTop: 4,
            }}
          >
            {recordingCenterTitle !== null || showBetaBadge ? (
              <View className="items-center" style={{ gap: 8, width: '100%' }}>
                {recordingCenterTitle !== null ? (
                  <Text
                    {...NON_SCALING_TEXT_PROPS}
                    className="text-center"
                    style={TITLE_STYLE}
                  >
                    {recordingCenterTitle ?? t('onboarding:record.title')}
                  </Text>
                ) : null}
                {showBetaBadge ? (
                  <BetaBadge size="large" testID="record-step-beta-badge" />
                ) : null}
              </View>
            ) : null}
            {recordingCenterSubtitle ? (
              <Text
                {...NON_SCALING_TEXT_PROPS}
                className="text-center"
                style={SUBTITLE_STYLE}
              >
                {recordingCenterSubtitle}
              </Text>
            ) : null}
          </View>
          {uiState.step === 'ready' &&
            recordingInstruction != null &&
            recordingInstruction.trim().length > 0 && (
              <VowelInstructionCard label={recordingInstruction} />
            )}
          <View
            className=""
            style={{
              borderRadius: CONTROL_BORDER_RADIUS,
              padding: CONTENT_BOX_PADDING,
              gap: CONTENT_BOX_GAP,
              width: '100%',
              backgroundColor: colors.background,
            }}
          >
            {uiState.step === 'ready' && (
              <RecordReadyState
                instruction={t('onboarding:record.ready_instruction')}
                playPianoLabel={t('onboarding:record.play_piano')}
                playExampleLabel={t('onboarding:record.play_example')}
                playingPianoLabel={t('onboarding:record.playing_piano')}
                playingExampleLabel={t('onboarding:record.playing_example')}
                startRecordingLabel={t('onboarding:record.start_recording')}
                onPlayPiano={handlePlayPiano}
                onPlayExample={handlePlayExample}
                onStartRecording={handleStartRecording}
                status={{
                  isExamplePlaying,
                  isHelperActionPending: uiState.isHelperActionPending,
                  isPianoPlaying,
                  isStartingRecording: uiState.isStartingRecording,
                }}
              />
            )}

            {uiState.step === 'recording' && (
              <RecordRecordingState
                statusLabel={t('onboarding:record.recording_status')}
                elapsedLabel={elapsedRecordingTimeLabel}
                stopRecordingLabel={t('onboarding:record.stop_recording')}
                onStopRecording={handleStopRecording}
                isStopPending={uiState.isStoppingRecording}
              />
            )}

            {uiState.step === 'succeeded' && (
              <RecordSucceededState
                statusLabel={t('onboarding:record.succeeded_status')}
                listenLabel={t('onboarding:record.listen_to_recording')}
                stopPlaybackLabel={t('onboarding:record.stop_playback')}
                redoLabel={t('onboarding:record.redo_recording')}
                onListen={handleListenToRecording}
                onRedo={handleRedoRecording}
                isRecordedAudioPlaying={isRecordedAudioPlaying}
                isListeningPending={uiState.isListeningPending}
                hasRecordedClip={uiState.recordedAudioUri != null}
              />
            )}

            {uiState.step === 'ready' && uiState.failedHelperAction != null && (
              <View
                className="rounded-[8px] px-4 py-3"
                style={{ backgroundColor: colors.errorBackground, gap: 8 }}
              >
                <Text
                  {...NON_SCALING_TEXT_PROPS}
                  style={[body3, { color: colors.textHeader }]}
                >
                  {helperActionErrorMessage}
                </Text>
                <Pressable
                  className="self-start rounded-[8px] border px-3 py-2"
                  style={{
                    borderColor: colors.textHeader,
                    borderRadius: CONTROL_BORDER_RADIUS,
                  }}
                  onPress={handleRetryHelperAction}
                >
                  <Text
                    {...NON_SCALING_TEXT_PROPS}
                    style={[label2, { color: colors.textHeader }]}
                  >
                    {t('onboarding:record.retry_helper_action')}
                  </Text>
                </Pressable>
              </View>
            )}

            {recordFlowErrorMessage != null && (
              <View
                className="rounded-[8px] px-4 py-3"
                style={{ backgroundColor: colors.errorBackground, gap: 8 }}
              >
                <Text
                  {...NON_SCALING_TEXT_PROPS}
                  style={[body3, { color: colors.textHeader }]}
                >
                  {recordFlowErrorMessage}
                </Text>
              </View>
            )}
          </View>
        </View>
      </ScrollView>

      {uiState.step === 'succeeded' && (
        <View
          style={{ paddingHorizontal: 24, paddingBottom: footerPaddingBottom }}
        >
          <Button
            label={t('onboarding:check_in.next_cta')}
            onPress={onNext}
            variant="primary"
            size="md"
            loading={isSubmitting}
            disabled={isNextDisabled}
            containerClassName="w-full"
          />
        </View>
      )}
    </View>
  )
}

function KeepAwakeController() {
  useKeepAwake()
  return null
}

interface VowelInstructionCardProps {
  label: string
}

function VowelInstructionCard({ label }: VowelInstructionCardProps) {
  return (
    <View
      className="flex-row items-center rounded-[8px]"
      style={{
        gap: 12,
        minHeight: 64,
        paddingHorizontal: 16,
        paddingVertical: 12,
        width: '100%',
        backgroundColor: colors.background,
      }}
    >
      <Icon
        name={symbolName('person.wave.2', 'record_voice_over')}
        size={24}
        color="muted"
      />
      <Text
        {...NON_SCALING_TEXT_PROPS}
        style={[
          body3,
          {
            color: colors.textBody,
            flex: 1,
            lineHeight: 18,
            letterSpacing: 0,
          },
        ]}
      >
        {label}
      </Text>
    </View>
  )
}
